import { useState, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Plus,
  Search,
  Upload,
  Download,
  Trash2,
  Edit2,
  User,
  Users,
  Tag,
  X
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactsPage() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [formData, setFormData] = useState({ name: "", phone_number: "", tags: [] });
  const [tagInput, setTagInput] = useState("");

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/contacts`);
      setContacts(response.data);
    } catch (error) {
      console.error("Error fetching contacts:", error);
      toast.error("Failed to load contacts");
    } finally {
      setLoading(false);
    }
  };

  // Get all unique tags from contacts
  const allTags = [...new Set(contacts.flatMap(c => c.tags || []))];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.phone_number.trim()) {
      toast.error("Phone number is required");
      return;
    }

    try {
      if (editingContact) {
        await axios.put(`${API}/contacts/${editingContact.id}`, formData);
        toast.success("Contact updated");
      } else {
        await axios.post(`${API}/contacts`, formData);
        toast.success("Contact added");
      }
      setDialogOpen(false);
      setEditingContact(null);
      setFormData({ name: "", phone_number: "", tags: [] });
      setTagInput("");
      fetchContacts();
    } catch (error) {
      console.error("Error saving contact:", error);
      toast.error(error.response?.data?.detail || "Failed to save contact");
    }
  };

  const handleDelete = async (contactId) => {
    if (!window.confirm("Are you sure you want to delete this contact?")) return;

    try {
      await axios.delete(`${API}/contacts/${contactId}`);
      toast.success("Contact deleted");
      fetchContacts();
    } catch (error) {
      console.error("Error deleting contact:", error);
      toast.error("Failed to delete contact");
    }
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setFormData({ 
      name: contact.name || "", 
      phone_number: contact.phone_number,
      tags: contact.tags || []
    });
    setDialogOpen(true);
  };

  const handleAddTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !formData.tags.includes(tag)) {
      setFormData({ ...formData, tags: [...formData.tags, tag] });
    }
    setTagInput("");
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tagToRemove) });
  };

  const handleExport = () => {
    const csvContent = [
      ["Name", "Phone Number", "Tags", "Source", "Created At"].join(","),
      ...contacts.map(c => [
        `"${c.name || ""}"`,
        `"${c.phone_number}"`,
        `"${(c.tags || []).join(';')}"`,
        `"${c.source || ""}"`,
        `"${c.created_at || ""}"`
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `contacts_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Contacts exported");
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const text = event.target?.result;
        const lines = text.split("\n").slice(1); // Skip header
        const importedContacts = [];

        for (const line of lines) {
          if (!line.trim()) continue;
          // Parse CSV: Name, Phone, Tags
          const match = line.match(/^"?([^"]*)"?,\s*"?([^"]*)"?,?\s*"?([^"]*)"?/);
          if (match) {
            const [, name, phone, tagsStr] = match;
            if (phone.trim()) {
              const tags = tagsStr ? tagsStr.split(';').map(t => t.trim()).filter(Boolean) : [];
              importedContacts.push({
                name: name.trim(),
                phone_number: phone.trim(),
                tags: tags
              });
            }
          }
        }

        if (importedContacts.length === 0) {
          toast.error("No valid contacts found in file");
          return;
        }

        const response = await axios.post(`${API}/contacts/import`, { contacts: importedContacts });
        toast.success(`Imported ${response.data.imported} contacts, ${response.data.skipped} updated`);
        fetchContacts();
      } catch (error) {
        console.error("Import error:", error);
        toast.error("Failed to import contacts");
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const filteredContacts = contacts.filter(c => {
    const matchesSearch = c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.phone_number?.includes(searchQuery);
    const matchesTag = !tagFilter || (c.tags || []).includes(tagFilter);
    return matchesSearch && matchesTag;
  });

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />

      <main className="flex-1 overflow-hidden">
        <div className="h-full p-6">
          <Card className="h-full flex flex-col">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-2xl flex items-center gap-2">
                    <Users className="h-6 w-6 text-primary" />
                    Contacts
                  </CardTitle>
                  <CardDescription>
                    Manage your customer contacts ({contacts.length} total)
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExport}
                    disabled={contacts.length === 0}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export
                  </Button>
                  <label>
                    <Button variant="outline" size="sm" asChild>
                      <span>
                        <Upload className="h-4 w-4 mr-2" />
                        Import
                      </span>
                    </Button>
                    <input
                      type="file"
                      accept=".csv"
                      className="hidden"
                      onChange={handleImport}
                    />
                  </label>
                  <Button
                    size="sm"
                    onClick={() => {
                      setEditingContact(null);
                      setFormData({ name: "", phone_number: "", tags: [] });
                      setTagInput("");
                      setDialogOpen(true);
                    }}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Contact
                  </Button>
                </div>
              </div>

              {/* Search and Tag Filter */}
              <div className="flex gap-4 mt-4">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name or phone..."
                    className="pl-9"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                {allTags.length > 0 && (
                  <select
                    className="px-3 py-2 border rounded-md text-sm bg-background"
                    value={tagFilter}
                    onChange={(e) => setTagFilter(e.target.value)}
                  >
                    <option value="">All Tags</option>
                    {allTags.map(tag => (
                      <option key={tag} value={tag}>{tag}</option>
                    ))}
                  </select>
                )}
              </div>
            </CardHeader>

            <CardContent className="flex-1 overflow-hidden">
              <ScrollArea className="h-full">
                {loading ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Loading contacts...
                  </div>
                ) : filteredContacts.length === 0 ? (
                  <div className="text-center py-12">
                    <User className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
                    <p className="text-muted-foreground">
                      {searchQuery || tagFilter ? "No contacts found" : "No contacts yet"}
                    </p>
                    <p className="text-sm text-muted-foreground/70">
                      {searchQuery || tagFilter ? "Try a different search" : "Contacts are auto-added when customers message you"}
                    </p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12"></TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Phone Number</TableHead>
                        <TableHead>Tags</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead>Added</TableHead>
                        <TableHead className="w-24 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredContacts.map((contact) => (
                        <TableRow key={contact.id}>
                          <TableCell>
                            <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center">
                              <User className="h-4 w-4 text-slate-500" />
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">
                            {contact.name || <span className="text-muted-foreground italic">No name</span>}
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {contact.phone_number}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {(contact.tags || []).map(tag => (
                                <Badge key={tag} variant="secondary" className="text-xs">
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground capitalize">
                            {contact.source || "-"}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {contact.created_at ? new Date(contact.created_at).toLocaleDateString() : "-"}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEdit(contact)}
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(contact.id)}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Add/Edit Contact Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingContact ? "Edit Contact" : "Add Contact"}
            </DialogTitle>
            <DialogDescription>
              {editingContact ? "Update contact details" : "Add a new contact to your list"}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  placeholder="Contact name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  placeholder="+91 9876543210"
                  value={formData.phone_number}
                  onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                  className="mt-1"
                  required
                />
              </div>
              <div>
                <Label htmlFor="tags">Tags</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    id="tags"
                    placeholder="Add tag..."
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddTag();
                      }
                    }}
                  />
                  <Button type="button" variant="outline" onClick={handleAddTag}>
                    <Tag className="h-4 w-4" />
                  </Button>
                </div>
                {formData.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {formData.tags.map(tag => (
                      <Badge key={tag} variant="secondary" className="text-xs pr-1">
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="ml-1 hover:text-destructive"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">
                {editingContact ? "Update" : "Add Contact"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
